import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchDocumentsAndMessagesApi } from "../../../api/client";
import { documentsAndMessagesKeys } from "./documentsAndMessagesKeys";
import type { DocumentsAndMessagesResponse } from "./types";

/**
 * Shared queryOptions for a deal's aggregated documents & messages (key +
 * queryFn). Backed by the generated WorkbenchDocsMessagesApi.getDocsMessages,
 * which composes documents from the appxtender/piston legs plus messages into a
 * single response. The generated method is typed as `object` (the BFF client
 * ships no models for this endpoint), so the response is cast to the
 * hand-written `DocumentsAndMessagesResponse`. `staleTime` matches the
 * endpoint's `meta.cacheTtlSeconds` (30s) and the app-wide convention.
 *
 * The route `approvalId` is passed straight through as the `appId` path segment,
 * matching the sibling `applications/{…}` status + VLA-lock reads.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @param {string} approvalId - The deal / approval identifier (used as appId).
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function documentsAndMessagesQuery(approvalId: string) {
  return queryOptions({
    queryKey: documentsAndMessagesKeys.detail({ approvalId }),
    queryFn: async ({ signal }): Promise<DocumentsAndMessagesResponse> => {
      const response = await workbenchDocumentsAndMessagesApi.getDocsMessages(approvalId, { signal });
      return response.data as DocumentsAndMessagesResponse;
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the aggregated documents & messages for a deal. Disabled (never fires)
 * until the approval id is present, so callers can render an empty/loading state
 * instead of issuing a doomed `/applications//docs-messages` request.
 * @param {string} approvalId - The deal / approval identifier (used as appId).
 * @returns {ReturnType<typeof useQuery<DocumentsAndMessagesResponse>>} The TanStack Query result.
 */
export function useDocumentsAndMessages(approvalId: string) {
  return useQuery(documentsAndMessagesQuery(approvalId));
}
